import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Heading,
  IconButton,
  Loader,
  Flex,
  Label,
  Text,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  type TableColumn,
} from '@vibe/core';
import { Add, Download, Retry } from '@vibe/icons';
import {
  getLeaveRequests,
  getLeaveTypes,
  recallLeaveRequest,
  type LeaveRequest,
  type LeaveRequestStatus,
  type LeaveType,
} from '../../api/leave.api';
import { fetchMondayUsers, type MondayUser } from '../../api/monday.api';
import { formatDate } from '../../utils/dates';
import { useLeaveRequestStore, type StatusFilter } from '../../stores/leaveRequestStore';

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'recalling', label: 'Recalling' },
  { key: 'recalled', label: 'Recalled' },
];

const STATUS_LABEL_COLOR: Record<
  LeaveRequestStatus,
  'EXPLOSIVE' | 'WORKING_ORANGE' | 'DONE_GREEN' | 'STUCK_RED' | 'DARK'
> = {
  pending: 'WORKING_ORANGE',
  approved: 'DONE_GREEN',
  rejected: 'STUCK_RED',
  recalling: 'EXPLOSIVE',
  recalled: 'DARK',
};

const TABLE_COLUMNS: TableColumn[] = [
  { id: 'type', title: 'Type', width: { min: 120, max: '1fr' } },
  { id: 'dates', title: 'Dates', width: { min: 180, max: '1fr' } },
  { id: 'days', title: 'Days', width: 70 },
  { id: 'note', title: 'Note', width: { min: 60, max: '1.5fr' } },
  { id: 'created_by', title: 'Created by', width: { min: 100, max: '0.8fr' } },
  { id: 'approver', title: 'Approver', width: { min: 140, max: '1fr' } },
  { id: 'status', title: 'Status', width: 110 },
  { id: 'actions', title: 'Actions', width: { min: 120, max: '1fr' } },
];

// ─── Props ────────────────────────────────────────────────────────────────────

interface MyLeavesTabProps {
  currentUserId: number | null;
  onCreateNew?: () => void;
  /** Increment to force a data refresh (e.g. after creating a leave request) */
  refreshKey?: number;
}

// ─── Component ────────────────────────────────────────────────────────────────

const MyLeavesTab: React.FC<MyLeavesTabProps> = ({
  currentUserId,
  onCreateNew,
  refreshKey,
}) => {
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [users, setUsers] = useState<Map<string, MondayUser>>(new Map());
  const [leaveTypes, setLeaveTypes] = useState<Map<string, LeaveType>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const statusFilter = useLeaveRequestStore((s) => s.myLeavesStatus);
  const setStatusFilter = useLeaveRequestStore((s) => s.setMyLeavesStatus);
  const page = useLeaveRequestStore((s) => s.myLeavesPage);
  const setPage = useLeaveRequestStore((s) => s.setMyLeavesPage);
  const pageSize = useLeaveRequestStore((s) => s.myLeavesPageSize);
  const total = useLeaveRequestStore((s) => s.myLeavesTotal);
  const setTotal = useLeaveRequestStore((s) => s.setMyLeavesTotal);

  const fetchRequests = useCallback(async () => {
    if (currentUserId == null) return;
    setIsLoading(true);
    setError(null);
    try {
      const params = {
        userId: currentUserId,
        ...(statusFilter !== 'all' && { status: statusFilter }),
        page,
        pageSize,
      };
      const [result, allUsers, types] = await Promise.all([
        getLeaveRequests(params),
        fetchMondayUsers(),
        getLeaveTypes(),
      ]);
      setRequests(result.data);
      setTotal(result.total);
      // Build lookup maps
      const userMap = new Map<string, MondayUser>();
      allUsers.forEach((u) => userMap.set(String(u.id), u));
      setUsers(userMap);
      const typeMap = new Map<string, LeaveType>();
      types.forEach((t) => typeMap.set(t.id, t));
      setLeaveTypes(typeMap);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load leave requests';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId, statusFilter, page, pageSize, setTotal, refreshKey]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Resolve display names
  const getUserName = (mondayUserId: string): string =>
    users.get(mondayUserId)?.name ?? `User ${mondayUserId}`;

  const getLeaveTypeName = (leaveTypeId: string): string =>
    leaveTypes.get(leaveTypeId)?.name ?? 'Unknown';

  const handleRecall = useCallback(
    async (id: string) => {
      try {
        await recallLeaveRequest(id);
        await fetchRequests();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to recall leave request';
        setError(message);
      }
    },
    [fetchRequests],
  );

  const renderActions = useCallback(
    (req: LeaveRequest) => {
      // Owner can recall pending or approved leaves
      if (req.status === 'pending' || req.status === 'approved') {
        return (
          <Button
            size={Button.sizes.SMALL}
            kind={Button.kinds.TERTIARY}
            leftIcon={Retry}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handleRecall(req.id);
            }}
          >
            Recall
          </Button>
        );
      }
      return null;
    },
    [handleRecall],
  );

  // Pagination
  const totalPages = Math.ceil(total / pageSize);
  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  const emptyState = (
    <div className="rp-leave-request-list__empty">
      <Text type={Text.types.TEXT1} style={{ color: '#676879' }}>
        No leave requests found.
      </Text>
    </div>
  );

  const errorState = (
    <div className="rp-leave-request-list__error">
      <Text style={{ color: '#e2445c' }}>{error}</Text>
    </div>
  );

  return (
    <div className="rp-leave-request-list">
      {/* Header */}
      <Flex
        justify={Flex.justify.SPACE_BETWEEN}
        align={Flex.align.CENTER}
        className="rp-leave-request-list__header"
      >
        <Heading type={Heading.types.H3}>My Leaves</Heading>
        <Flex gap={Flex.gaps.SMALL} align={Flex.align.CENTER}>
          <IconButton
            icon={Download}
            size={IconButton.sizes.SMALL}
            ariaLabel="Export my leave requests"
            onClick={async () => {
              try {
                const {
                  createWorkbook, addTitleRow, styleHeaderRow, styleDataRow,
                  setColumnWidths, downloadWorkbook, fillCell, formatDateForExcel,
                  getStatusColors, COLORS,
                } = await import('../../utils/export-report-excel');

                const wb = createWorkbook();
                const ws = wb.addWorksheet('My Leave Requests');
                addTitleRow(ws, 'My Leave Requests', '', '');

                const headers = ['Type', 'From', 'To', 'Days', 'Note', 'Created by', 'Approver', 'Status'];
                const headerRow = ws.addRow(headers);
                styleHeaderRow(ws, headerRow);
                setColumnWidths(ws, [16, 12, 12, 8, 24, 18, 18, 14]);

                for (const req of requests) {
                  const typeName = leaveTypes.get(req.leave_type_id)?.name ?? req.leave_type_id;
                  const approverName = users.get(req.approver_user_id)?.name ?? req.approver_user_id;
                  const createdByName = req.created_by_user_id
                    ? (users.get(req.created_by_user_id)?.name ?? req.created_by_user_id)
                    : '';

                  const row = ws.addRow([
                    typeName,
                    formatDateForExcel(req.date_from),
                    formatDateForExcel(req.date_to),
                    req.total_days,
                    req.note ?? '',
                    createdByName,
                    approverName,
                    req.status.charAt(0).toUpperCase() + req.status.slice(1),
                  ]);
                  styleDataRow(row);

                  const statusColors = getStatusColors(req.status === 'recalled' || req.status === 'recalling' ? 'rejected' : req.status === 'pending' ? 'submitted' : req.status);
                  if (statusColors) {
                    fillCell(row.getCell(8), statusColors.bg, statusColors.font, true);
                  }
                }

                await downloadWorkbook(wb, 'my-leave-requests.xlsx');
              } catch (err) {
                console.error('Excel export failed:', err);
              }
            }}
          />
          <Button
            size={Button.sizes.SMALL}
            leftIcon={Add}
            onClick={onCreateNew}
          >
            Request Leave
          </Button>
        </Flex>
      </Flex>

      {/* Status filter */}
      <Flex
        gap={Flex.gaps.SMALL}
        className="rp-leave-request-list__filters"
      >
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key}
            className={`rp-leave-request-list__filter-btn ${
              statusFilter === f.key
                ? 'rp-leave-request-list__filter-btn--active'
                : ''
            }`}
            onClick={() => setStatusFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </Flex>

      {/* Table */}
      {isLoading ? (
        <div className="rp-leave-request-list__loading">
          <Loader size={Loader.sizes.MEDIUM} />
        </div>
      ) : (
        <Table
          columns={TABLE_COLUMNS}
          errorState={errorState}
          emptyState={emptyState}
          dataState={{ isLoading, isError: !!error }}
        >
          <TableHeader>
            {TABLE_COLUMNS.map((col) => (
              <TableHeaderCell
                key={col.id}
                title={col.title}
                {...(col.id === 'created_by' ? {
                  infoContent: 'Shows who created the leave request. "—" means you requested it yourself. A name means it was created on your behalf by another user (e.g., your manager).',
                } : {})}
              />
            ))}
          </TableHeader>
          <TableBody>
            {requests.map((req) => (
              <TableRow key={req.id}>
                <TableCell>{getLeaveTypeName(req.leave_type_id)}</TableCell>
                <TableCell>
                  {formatDate(req.date_from, 'MMM d')} &ndash;{' '}
                  {formatDate(req.date_to, 'MMM d, yyyy')}
                </TableCell>
                <TableCell>
                  {req.total_days}
                  {req.capacity_type === 'half_day' ? ' (half)' : ''}
                </TableCell>
                <TableCell>
                  {req.note ? (
                    <Text
                      type={Text.types.TEXT2}
                      tooltipProps={{
                        content: req.note,
                        style: { zIndex: 9999 },
                      }}
                      style={{
                        color: 'var(--primary-text-color, #323338)',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        wordBreak: 'break-word',
                      }}
                    >
                      {req.note}
                    </Text>
                  ) : (
                    <Text type={Text.types.TEXT2} style={{ color: '#adadad' }}>
                      —
                    </Text>
                  )}
                </TableCell>
                <TableCell>
                  {req.created_by_user_id && req.created_by_user_id !== req.monday_user_id ? (
                    <Text type={Text.types.TEXT2} ellipsis style={{ color: 'var(--primary-text-color, #323338)' }}>
                      {getUserName(req.created_by_user_id)}
                    </Text>
                  ) : (
                    <Text type={Text.types.TEXT2} style={{ color: '#adadad' }}>—</Text>
                  )}
                </TableCell>
                <TableCell>{getUserName(req.approver_user_id)}</TableCell>
                <TableCell>
                  <Label
                    text={
                      req.status.charAt(0).toUpperCase() + req.status.slice(1)
                    }
                    color={Label.colors[STATUS_LABEL_COLOR[req.status]]}
                  />
                </TableCell>
                <TableCell>{renderActions(req)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Pagination */}
      {total > 0 && (
        <div className="rp-leave-request-list__pagination">
          <Text type={Text.types.TEXT2} color={Text.colors.SECONDARY}>
            {showingFrom}–{showingTo} of {total}
          </Text>
          <Flex gap={Flex.gaps.XS}>
            <Button
              size={Button.sizes.SMALL}
              kind={Button.kinds.TERTIARY}
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Prev
            </Button>
            <Button
              size={Button.sizes.SMALL}
              kind={Button.kinds.TERTIARY}
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </Flex>
        </div>
      )}
    </div>
  );
};

export default MyLeavesTab;
